import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, Sparkles } from "lucide-react";

const ytThumb = (u) => {
  const m = (u || "").match(/(?:youtu\.be\/|v=|youtube\.com\/embed\/|shorts\/)([A-Za-z0-9_-]{6,})/);
  return m ? `https://img.youtube.com/vi/${m[1]}/maxresdefault.jpg` : null;
};
export const filmThumb = (f) => f.cover_url || ytThumb(f.youtube_url);

export function FeaturedBanner({ film }) {
  const bg = filmThumb(film);
  return (
    <section className="relative z-10 overflow-hidden" data-testid="showo-featured">
      <div className="absolute inset-0">
        {bg && <img src={bg} alt="" className="w-full h-full object-cover scale-105 animate-[pulse_12s_ease-in-out_infinite] opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#08081a] via-[#08081a]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08081a] via-transparent to-[#08081a]/40" />
      </div>
      <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-14 sm:pt-24 sm:pb-20">
        <div className="fl-mono text-[11px] tracking-widest uppercase text-pink-400">// Featured on Showo · {(film.category || "").replace("_", " ")}</div>
        <h1 className="fl-display text-4xl sm:text-5xl lg:text-6xl font-black mt-2 tracking-tight max-w-2xl line-clamp-2" data-testid="showo-featured-title">{film.title}</h1>
        <div className="mt-2 text-sm text-white/60">{film.category === "music_video" ? film.artist : film.director}{film.genre ? ` · ${film.genre}` : ""}</div>
        {film.synopsis && <p className="text-slate-300 mt-3 max-w-xl line-clamp-3 text-sm sm:text-base">{film.synopsis}</p>}
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to={`/showo/f/${film.film_id}`} className="fl-btn !bg-white !text-black" data-testid="showo-featured-watch"><Play className="w-4 h-4 fill-black" /> Watch now</Link>
          <Link to="/showo/make" className="fl-btn ghost" data-testid="showo-featured-make"><Sparkles className="w-4 h-4" /> Make your own</Link>
        </div>
      </div>
    </section>
  );
}

export function FilmRow({ title, films, Card, id }) {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.85, behavior: "smooth" });
  if (!films.length) return null;
  return (
    <section className="relative z-10 mt-8 group/row" data-testid={`showo-row-${id}`}>
      <div className="max-w-6xl mx-auto px-5 flex items-end justify-between">
        <h2 className="fl-display text-base md:text-lg font-black">{title}</h2>
        <div className="hidden sm:flex gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
          <button type="button" onClick={() => scroll(-1)} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid={`showo-row-${id}-prev`}><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => scroll(1)} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid={`showo-row-${id}-next`}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <div ref={ref} className="mt-3 flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth px-5 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-w-6xl mx-auto">
        {films.map(f => (
          <div key={f.film_id} className="snap-start shrink-0 w-[62%] sm:w-[38%] md:w-[28%] lg:w-[23%] transition-transform duration-300 hover:-translate-y-1">
            <Card film={f} />
          </div>
        ))}
      </div>
    </section>
  );
}
